import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { NavigationError, provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';

import { routes } from './app.routes';
import { withCredentialsInterceptor } from './shared/interceptors/with-credentials.interceptor';
import { SessionRestoreService } from './features/auth/services/session-restore.service';
import { accessTokenInterceptor } from './shared/interceptors/access-token.interceptor';
import { debeRecargar } from './shared/utils/chunk-desactualizado';
import { ReturnUrlService } from './shared/services/return-url.service';

const CLAVE_RECARGA = 'muni:recarga-por-chunk';

// Si la vista a la que se navega no puede cargar su chunk (deploy nuevo con index.html
// cacheado), la navegación queda a medias y el usuario se queda donde estaba. Recargar una
// vez trae el index.html nuevo y la navegación vuelve a funcionar.
const recargarSiElChunkQuedoViejo = ({ error }: NavigationError): void => {
  let ultima: number | null = null;
  try {
    const guardado = sessionStorage.getItem(CLAVE_RECARGA);
    ultima = guardado ? Number(guardado) : null;
  } catch {
    // Storage bloqueado: se intenta la recarga igual, es la única salida.
  }
  if (!debeRecargar(error, Number.isFinite(ultima) ? ultima : null, Date.now())) {
    return;
  }
  try {
    sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()));
  } catch {
    // Sin storage no hay freno entre recargas, pero el patrón del error ya es específico.
  }
  location.reload();
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withFetch(), withInterceptors([withCredentialsInterceptor, accessTokenInterceptor])),
    provideAppInitializer(() => inject(SessionRestoreService).restore()),
    // Tiene que existir desde el arranque: anota en qué vista está el usuario para poder
    // devolverlo ahí después del login. Si naciera recién al abrirse el login, ya no sabría
    // de dónde venía.
    provideAppInitializer(() => {
      inject(ReturnUrlService);
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'enabled',
      }),
      withNavigationErrorHandler(recargarSiElChunkQuedoViejo),
    ),
  ],
};
